import { SocialNetworksId } from "@/interfaces";
import { PostStatsBreakdownEntry } from "@/services/bedrock/dtos/Stats";
import { kFormatter } from "@/utils/formatHelpers";

export type PerfMetric = "impressions" | "reactions" | "comments";

export interface PostAggregate {
  postId: number;
  playerId: number;
  playerName: string;
  thumbnailUrl: string | null;
  postedAt: string | null;
  networks: SocialNetworksId[];
  primaryNetwork: SocialNetworksId;
  externalPostUrl: string | null;
  impressions: number;
  reactions: number;
  comments: number;
  shares: number;
}

type Counts = Pick<PostAggregate, "impressions" | "reactions" | "comments" | "shares">;

export function liveEntries(breakdown: PostStatsBreakdownEntry[]): PostStatsBreakdownEntry[] {
  return breakdown.filter(entry => entry.status === "POSTED");
}

function timeOf(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

/** Ascending by posted time, undated entries last. */
function byPostedAsc(a: PostStatsBreakdownEntry, b: PostStatsBreakdownEntry): number {
  const ta = timeOf(a.postedTimestamp);
  const tb = timeOf(b.postedTimestamp);
  if (ta == null && tb == null) return 0;
  if (ta == null) return 1;
  if (tb == null) return -1;
  return ta - tb;
}

function entryCounts(entry: PostStatsBreakdownEntry): Counts {
  const stats = entry.latestStats;
  return {
    // Networks without an impressions count report reach, and those without a reactions rollup report likes.
    impressions: stats?.impressionCount ?? stats?.reachCount ?? 0,
    reactions: stats?.totalReactions ?? stats?.likeCount ?? 0,
    comments: stats?.commentCount ?? 0,
    shares: stats?.shareCount ?? 0,
  };
}

/** One aggregate per post, summing its live network entries; the earliest-posted network leads. */
export function groupByPost(breakdown: PostStatsBreakdownEntry[]): PostAggregate[] {
  const byPost = new Map<number, PostStatsBreakdownEntry[]>();
  liveEntries(breakdown).forEach(entry => {
    // Rows without a post id can't be grouped or opened.
    if (entry.postId == null) return;
    const entries = byPost.get(entry.postId) ?? [];
    entries.push(entry);
    byPost.set(entry.postId, entries);
  });

  return Array.from(byPost.entries()).map(([postId, entries]) => {
    const ordered = [...entries].sort(byPostedAsc);
    const first = ordered[0];
    const totals = ordered.reduce<Counts>(
      (sum, entry) => {
        const counts = entryCounts(entry);
        return {
          impressions: sum.impressions + counts.impressions,
          reactions: sum.reactions + counts.reactions,
          comments: sum.comments + counts.comments,
          shares: sum.shares + counts.shares,
        };
      },
      { impressions: 0, reactions: 0, comments: 0, shares: 0 }
    );
    return {
      postId,
      playerId: first.playerId,
      playerName: first.playerName,
      thumbnailUrl: ordered.find(entry => entry.thumbnailUrl)?.thumbnailUrl ?? null,
      postedAt: first.postedTimestamp || null,
      networks: ordered.map(entry => entry.socialNetworkId),
      primaryNetwork: first.socialNetworkId,
      externalPostUrl: first.externalPostUrl || null,
      ...totals,
    };
  });
}

export function totalsFromAggregates(aggregates: PostAggregate[]): Counts & { posts: number } {
  return aggregates.reduce(
    (sum, post) => ({
      posts: sum.posts + 1,
      impressions: sum.impressions + post.impressions,
      reactions: sum.reactions + post.reactions,
      comments: sum.comments + post.comments,
      shares: sum.shares + post.shares,
    }),
    { posts: 0, impressions: 0, reactions: 0, comments: 0, shares: 0 }
  );
}

export function perPostAverages(aggregates: PostAggregate[]): Counts {
  if (aggregates.length === 0) return { impressions: 0, reactions: 0, comments: 0, shares: 0 };
  const totals = totalsFromAggregates(aggregates);
  const n = aggregates.length;
  return {
    impressions: Math.round(totals.impressions / n),
    reactions: Math.round(totals.reactions / n),
    comments: Math.round(totals.comments / n),
    shares: Math.round(totals.shares / n),
  };
}

/** Newest first, undated posts last. */
function byPostedDesc(a: PostAggregate, b: PostAggregate): number {
  const ta = timeOf(a.postedAt);
  const tb = timeOf(b.postedAt);
  if (ta == null && tb == null) return 0;
  if (ta == null) return 1;
  if (tb == null) return -1;
  return tb - ta;
}

export function sortRecent(aggregates: PostAggregate[]): PostAggregate[] {
  return [...aggregates].sort(byPostedDesc);
}

export function sortTop(aggregates: PostAggregate[], metric: PerfMetric): PostAggregate[] {
  return [...aggregates].sort((a, b) => b[metric] - a[metric] || byPostedDesc(a, b));
}

/** Unlike kFormatter, 0 reads "0" rather than "--". */
export function formatCount(n: number): string {
  if (n === 0) return "0";
  return n > 999 ? kFormatter(n) : String(n);
}
